import { CxFeatureFlags } from "@/domain/feature-flags";
import { Skeleton } from "@chakra-ui/react";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { setBaseUrl } from "../../../../api/apiOSS";
import { setAuthTokenHeaders } from "../../../../api/auth";
import { EhrDetails } from "../../../../domain/ehr";
import {
  initialState,
  reducer,
  SharedState,
  SharedStateAction,
  SharedStateActionType,
} from "./reducer";

interface SharedStateContext {
  sharedState: SharedState;
  sharedDispatch: Dispatch<SharedStateAction>;
}

export const SharedStateContext = createContext<SharedStateContext>({
  sharedState: initialState,
  sharedDispatch: () => null,
});

export const SharedStateProvider = ({
  authToken,
  baseUrl,
  ehrDetails,
  isMetriportUser = false,
  isUserInSandbox = false,
  userEmail,
  featureFlags,
  children,
}: {
  authToken: string;
  baseUrl: string;
  ehrDetails: EhrDetails | undefined;
  featureFlags: CxFeatureFlags;
  isMetriportUser?: boolean;
  isUserInSandbox?: boolean;
  userEmail?: string;
  children: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}) => {
  const [sharedState, sharedDispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    function setShared() {
      setAuthTokenHeaders(authToken);
      setBaseUrl(baseUrl);
      sharedDispatch({
        type: SharedStateActionType.set,
        authToken,
        baseUrl,
        isUserInSandbox,
        ehrDetails,
        isMetriportUser,
        userEmail,
        isLoaded: true,
        featureFlags,
      });
    }
    setShared();
  }, [authToken, baseUrl, isUserInSandbox, isMetriportUser, userEmail, ehrDetails, featureFlags]);

  // Prevent that the UI/components are rendered before set the auth token
  if (!sharedState.isLoaded) return <Skeleton height="100vh" width="100vw" />;

  return (
    <SharedStateContext.Provider value={{ sharedState, sharedDispatch }}>
      {children}
    </SharedStateContext.Provider>
  );
};

export const useSharedContext = () => useContext(SharedStateContext);
